// frontend/src/pages/Simulations.tsx
import React, { useEffect, useRef, useState } from 'react';
import { Phone, ExternalLink, BarChart, Clock, TrendingUp, ChevronDown } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import LiveKitScene from '../components/LiveKitScene';
import type { AxiosError } from 'axios';
import { useAuth } from '../contexts/AuthContext';

interface SimulationScenario {
  id: number;
  nom: string;
  description: string;
  ordre: number;
}

interface ActiveSession {
  scenario: SimulationScenario;
  joinUrl: string;
  roomName: string;
  simulationId: number;
}

interface CallSimulation {
  id: number;
  scenario_id: number;
  score: number | null;
  feedback: string | null;
  duration: number;
  created_at: string;
}

interface Promotion {
  id: number;
  nom: string;
  date_debut: string;
  date_fin: string;
  is_active: boolean;
}

interface LiveKitEvent {
  id: number;
  event_type: string;
  room_name: string;
  participant_identity: string | null;
  created_at: string;
}

interface SimulationRequest {
  id: number;
  scenario_id: number;
  scenario_name: string;
  status: string;
  first_name: string;
  last_name: string;
  email: string;
}

export default function Simulations() {
  const { user } = useAuth();
  const [scenarios, setScenarios] = useState<SimulationScenario[]>([]);
  const [history, setHistory] = useState<CallSimulation[]>([]);
  const [events, setEvents] = useState<LiveKitEvent[]>([]);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [currentPromotion, setCurrentPromotion] = useState<Promotion | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeSession, setActiveSession] = useState<ActiveSession | null>(null);
  const [requests, setRequests] = useState<Record<number, SimulationRequest>>({});

  // --- UI: dropdown custom pour la sélection de promotion (pas de gris) ---
  const [promoOpen, setPromoOpen] = useState(false);
  const promoRef = useRef<HTMLDivElement | null>(null);

  // --- NEW: flags & util refs pour pré-débloquer l'audio/micro sans UI ---
  const prewarmedRef = useRef(false);
  const prewarmPromiseRef = useRef<Promise<void> | null>(null);

  // Pré-débloquer l'audio + micro en s'appuyant sur le clic "Démarrer" (gesture utilisateur)
  // Ne supprime aucune fonction existante — seulement ajouté et appelé au bon moment.
  const prewarmAudioAndMic = async () => {
    if (prewarmedRef.current) return;
    if (prewarmPromiseRef.current) return prewarmPromiseRef.current;

    prewarmPromiseRef.current = (async () => {
      try {
        // 1) AudioContext resume (certaines plateformes l'exigent)
        const AC: any = (window as any).AudioContext || (window as any).webkitAudioContext;
        if (AC) {
          const ctx = new AC();
          if (ctx.state === 'suspended' && typeof ctx.resume === 'function') {
            await ctx.resume();
          }
          // Crée un signal très court pour satisfaire certains navigateurs
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          gain.gain.value = 0.00001; // inaudible
          osc.connect(gain).connect(ctx.destination);
          osc.start();
          osc.stop(ctx.currentTime + 0.02);
        }

        // 2) Débloquer autoplay via un <audio> silencieux joué puis mis en pause
        // (ne crée rien en UI ; élément éphémère dans le DOM)
        const a = document.createElement('audio');
        a.setAttribute('playsinline', '');
        (a as any).disableRemotePlayback = true;
        a.autoplay = true;
        a.muted = true; // requis par certains navigateurs pour le premier play
        // data URI d'un WAV silencieux ultra court
        a.src =
          'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAAAAAAFVVVVU=';
        try {
          await a.play();
          a.pause();
        } catch {}
        a.remove();

        // 3) Demander le micro une fois (tracks stoppées immédiatement pour éviter side-effects)
        if (navigator.mediaDevices?.getUserMedia) {
          try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            stream.getTracks().forEach(t => t.stop());
          } catch {
            // Normal si l'utilisateur refuse — LiveKitScene gérera avec piste silencieuse
          }
        }

        prewarmedRef.current = true;
      } catch {
        // on ignore — la scène LiveKit a déjà un fallback solide
      }
    })();

    return prewarmPromiseRef.current;
  };

  useEffect(() => {
    fetchData();
    loadEvents();
    fetchRequests();
    const reqInterval = setInterval(fetchRequests, 5000);
    const interval = setInterval(loadEvents, 5000);
    return () => {
      clearInterval(interval);
      clearInterval(reqInterval);
    };
  }, []);

  // Fermer le menu au clic extérieur / ESC
  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (promoRef.current && !promoRef.current.contains(e.target as Node)) {
        setPromoOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setPromoOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const loadPromotionData = async (promo: Promotion) => {
    try {
      setLoading(true);
      const scenariosRes = await api.get(`/simulations/scenarios/${promo.id}`);
      const historyRes = await api.get('/simulations/history', {
        params: { promotion_id: promo.id }
      });

      setCurrentPromotion(promo);
      setScenarios(scenariosRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      toast.error('Impossible de charger les simulations');
    } finally {
      setLoading(false);
    }
  };

  const loadEvents = async () => {
    try {
      const res = await api.get('/webhooks/livekit/events');
      setEvents(res.data);
    } catch (error) {
      console.error('Erreur récupération événements LiveKit', error);
    }
  };

  const fetchRequests = async () => {
    try {
      const res = await api.get('/simulations/my-requests');
      const map: Record<number, SimulationRequest> = {};
      res.data.forEach((r: SimulationRequest) => {
        map[r.scenario_id] = r;
      });
      setRequests(map);
    } catch (e) {
      console.error('Erreur chargement demandes', e);
    }
  };

  const requestAuth = async (scenario: SimulationScenario) => {
    try {
      const res = await api.post('/simulations/request', {
        scenario_id: scenario.id,
        first_name: user?.prenom,
        last_name: user?.nom,
        email: user?.email,
      });
      setRequests(prev => ({ ...prev, [scenario.id]: res.data }));
      toast.success('Demande envoyée');
    } catch {
      toast.error('Erreur lors de la demande');
    }
  };

  const fetchData = async () => {
    try {
      // Récupérer les promotions de l'utilisateur
      const promotionsRes = await api.get('/promotions/my-promotions');
      const allPromotions: Promotion[] = promotionsRes.data;
      setPromotions(allPromotions);

      let selectedPromotion: Promotion | undefined;

      for (const promo of allPromotions) {
        const scenariosRes = await api.get(`/simulations/scenarios/${promo.id}`);
        if (scenariosRes.data.length > 0) {
          selectedPromotion = promo;
          setScenarios(scenariosRes.data);
          break;
        }
      }

      if (!selectedPromotion && allPromotions.length > 0) {
        selectedPromotion = allPromotions[0];
        const scenariosRes = await api.get(`/simulations/scenarios/${selectedPromotion.id}`);
        setScenarios(scenariosRes.data);
      }

      if (selectedPromotion) {
        await loadPromotionData(selectedPromotion);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des simulations:', error);
      toast.error('Impossible de charger les simulations');
      setLoading(false);
    }
  };

  const startSimulation = async (scenario: SimulationScenario) => {
    try {
      if (!currentPromotion) {
        toast.error('Aucune formation sélectionnée');
        return;
      }
      const req = requests[scenario.id];
      if (!req || req.status !== 'approved') {
        toast.error('Demande non autorisée');
        return;
      }

      //  AVANT toute requête réseau : pré-débloquer audio/micro en profitant du clic utilisateur
      await prewarmAudioAndMic();

      // Générer un call_sid unique
      const callSid = `SIM_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const roomName = callSid;

      const res = await api.post('/simulations/start', {
        scenario_id: scenario.id,
        call_sid: callSid,
        duration: 0,
        room_name: roomName,
        request_id: req.id,
      }, {
        params: { promotion_id: currentPromotion!.id }
      });

      setActiveSession({
        scenario,
        joinUrl: res.data.join_url,
        roomName: res.data.room_name,
        simulationId: res.data.simulation.id,
      });

      toast.success('Simulation démarrée ! Votre appel sera analysé automatiquement.');
    } catch (error) {
      const err = error as AxiosError<{ detail?: string }>;
      if (err.response?.data?.detail) {
        toast.error(err.response.data.detail);
      } else {
        toast.error('Erreur lors du démarrage de la simulation');
      }
    }
  };

  const stopSimulation = async () => {
    if (!activeSession) return;
    try {
      await api.post('/simulations/stop', {
        simulation_id: activeSession.simulationId,
        room_name: activeSession.roomName,
      });
      loadEvents();
    } catch (error) {
      console.error('Erreur arrêt simulation', error);
    } finally {
      setActiveSession(null);
    }
  };

  const stopSimulationKeepSession = async () => {
    if (!activeSession) return;
    try {
      await api.post('/simulations/stop', {
        simulation_id: activeSession.simulationId,
        room_name: activeSession.roomName,
      });
      loadEvents();
      // Rafraîchir les données (historique, statistiques)
      await fetchData();
      fetchRequests();
    } catch (error) {
      console.error('Erreur arrêt simulation', error);
    } finally {
      setActiveSession(null);
    }
  };

  const getAverageScore = () => {
    const scores = history.filter(h => h.score !== null).map(h => h.score!);
    if (scores.length === 0) return 0;
    return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!currentPromotion) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <p className="text-yellow-800">
          Vous devez être inscrit à une formation pour accéder aux simulations
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900">Simulations d'appel</h1>
        <p className="mt-2 text-gray-600">
          Entraînez-vous avec des mises en situation réalistes
        </p>
      </div>

      {/* Sélection de la formation — dropdown custom (même largeur/hauteur, pas de gris) */}
      {promotions.length > 1 && (
        <div className="bg-white rounded-lg shadow p-6">
          <label className="block text-sm font-medium text-gray-700">
            Formation
          </label>

          <div
            ref={promoRef}
            className="relative mt-2 w-full"
          >
            {/* Bouton (mêmes dimensions que l'ancien select: p-2, rounded, full width) */}
            <button
              type="button"
              onClick={() => setPromoOpen((v) => !v)}
              className="
                w-full rounded border border-gray-300 bg-white
                px-3 py-2 text-left text-sm text-gray-900
                shadow-sm outline-none
                focus:border-primary focus:ring-4 focus:ring-primary/10
                flex items-center justify-between
              "
              aria-haspopup="listbox"
              aria-expanded={promoOpen}
            >
              <span className="truncate">
                {currentPromotion?.nom || 'Sélectionner une formation'}
              </span>
              <ChevronDown className={`ml-2 h-4 w-4 text-gray-500 transition-transform ${promoOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Menu */}
            {promoOpen && (
              <div
                role="listbox"
                className="
                  absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-gray-200
                  bg-white shadow-lg
                "
              >
                <ul className="max-h-60 overflow-auto py-1">
                  {promotions.map((promo) => {
                    const selected = currentPromotion?.id === promo.id;
                    return (
                      <li key={promo.id}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={selected}
                          onClick={() => {
                            setPromoOpen(false);
                            if (!selected) {
                              loadPromotionData(promo);
                            }
                          }}
                          className={`
                            w-full px-3 py-2 text-left text-sm
                            ${selected ? 'bg-primary/10 text-primary' : 'text-gray-800'}
                            hover:bg-primary/10 hover:text-primary
                            focus:bg-primary/10 focus:text-primary
                            outline-none transition
                          `}
                        >
                          {promo.nom}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Simulations réalisées</p>
              <p className="text-2xl font-bold text-gray-900">{history.length}</p>
            </div>
            <Phone className="h-8 w-8 text-primary" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Score moyen</p>
              <p className="text-2xl font-bold text-gray-900">{getAverageScore()}%</p>
            </div>
            <TrendingUp className="h-8 w-8 text-green-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Dernière simulation</p>
              <p className="text-2xl font-bold text-gray-900">
                {history.length > 0 
                  ? new Date(history[0].created_at).toLocaleDateString()
                  : '-'
                }
              </p>
            </div>
            <Clock className="h-8 w-8 text-blue-500" />
          </div>
        </div>
      </div>

      {/* Scénarios disponibles */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Scénarios disponibles</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {scenarios.map((scenario) => (
            <div
              key={scenario.id}
              className="border rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <h3 className="font-semibold text-gray-900">{scenario.nom}</h3>
              {scenario.description && (
                <p className="mt-2 text-sm text-gray-600">{scenario.description}</p>
              )}
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  const req = requests[scenario.id];
                  if (req && req.status === 'approved') {
                    // Profiter de ce clic (gesture) pour pré-débloquer audio/micro AVANT de rejoindre
                    await prewarmAudioAndMic();
                    startSimulation(scenario);
                  } else if (!req || req.status === 'denied' || req.status === 'finished') {
                    requestAuth(scenario);
                  } else {
                    toast('Demande en attente');
                  }
                }}
                className={`mt-4 w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${requests[scenario.id]?.status === 'approved' ? 'bg-primary hover:bg-primary-dark' : 'bg-gray-400 hover:bg-gray-400'}`}
              >
                <Phone className="h-4 w-4 mr-2" />
                {requests[scenario.id]?.status === 'approved' ? 'Démarrer' : requests[scenario.id]?.status === 'pending' ? 'En attente...' : 'Demander'}
              </button>
            </div>
          ))}
          {scenarios.length === 0 && (
            <p className="text-sm text-gray-600">Aucun scénario disponible</p>
          )}
        </div>
      </div>

      {/* Historique instantané */}
      {events.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Historique instantané</h2>
          <ul className="space-y-2">
            {events.map((ev) => (
              <li key={ev.id} className="text-sm text-gray-700">
                [{new Date(ev.created_at).toLocaleTimeString()}] {ev.event_type}
                {ev.participant_identity ? ` - ${ev.participant_identity}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Historique */}
      {history.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Historique des simulations</h2>
          <div className="space-y-4">
            {history.map((simulation) => {
              const scenario = scenarios.find(s => s.id === simulation.scenario_id);
              const startDate = new Date(simulation.created_at);
              const endDate = new Date(startDate.getTime() + simulation.duration * 1000);
              
              return (
                <div
                  key={simulation.id}
                  className="border rounded-lg p-4"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">
                        {scenario?.nom || 'Simulation'}
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {startDate.toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                      <p className="text-sm text-gray-600">
                        Fin : {endDate.toLocaleTimeString('fr-FR', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                      
                      {simulation.score !== null ? (
                        <>
                          <div className="mt-3 flex items-center">
                            <div className="flex-1 bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full ${
                                  simulation.score >= 80 
                                    ? 'bg-green-500' 
                                    : simulation.score >= 60 
                                    ? 'bg-yellow-500' 
                                    : 'bg-red-500'
                                }`}
                                style={{ width: `${simulation.score}%` }}
                              />
                            </div>
                            <span className="ml-3 text-sm font-medium">
                              {simulation.score}%
                            </span>
                          </div>
                          
                          {simulation.feedback && (
                            <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                {simulation.feedback}
                              </p>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="mt-3 p-3 bg-yellow-50 rounded-lg">
                          <p className="text-sm text-yellow-800">
                            Analyse en cours... Les résultats apparaîtront dans quelques minutes.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {activeSession && (
        <LiveKitScene
          joinUrl={activeSession.joinUrl}
          scenarioName={activeSession.scenario?.nom || 'Simulation'}
          onClose={() => setActiveSession(null)}
          onDisconnect={stopSimulationKeepSession}
        />
      )}
    </div>
  );
}
